import { useAuth } from 'vue-auth3';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useRouter } from 'vue-router'; // Assuming you're using Vue Router
import { useNotificationStore } from '@/stores/app/NotificationStore';

export function useAuthHttp() {
    const auth = useAuth();
    const router = useRouter();

    const request = async <T = any>(config: AxiosRequestConfig, fullResponse = false): Promise<T | AxiosResponse<T>> => {
        const defaultConfig: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        try {
            const response: AxiosResponse<T> = await auth.http({
                ...defaultConfig,
                ...config,
                headers: {
                    ...defaultConfig.headers,
                    ...config.headers,
                },
                method: config.method || 'get',
            });

            // Conditionally return the full response or just the data
            return fullResponse ? response : response.data;
        } catch (error) {
            // Check if the error is due to an expired or invalid token
            if (error.response && error.response.status === 401) {
                // 1. Always attempt to refresh the token first
                try {
                    await auth.refresh();
                    const retryResponse: AxiosResponse<T> = await auth.http({
                        ...defaultConfig,
                        ...config,
                        headers: { ...defaultConfig.headers, ...config.headers },
                        method: config.method || 'get',
                    });
                    return fullResponse ? retryResponse : retryResponse.data;
                } catch (refreshError) {
                    // Refresh failed -> Downgrade to Guest if allowed
                    if (config.ignoreAuthRedirect) {
                        console.warn('[useAuthHttp] Refresh failed on public endpoint. Downgrading to guest.');
                        auth.logout({ makeRequest: false, redirect: false });

                        const retryConfig = {
                            ...defaultConfig, ...config,
                            headers: { ...defaultConfig.headers, ...config.headers }
                        };
                        delete retryConfig.headers['Authorization'];

                        try {
                            const guestResp = await auth.http({
                                ...retryConfig,
                                method: config.method || 'get',
                            });
                            return fullResponse ? guestResp : guestResp.data;
                        } catch (guestErr) {
                            throw guestErr;
                        }
                    }
                    console.error('Token refresh failed:', refreshError);

                    console.error('Token refresh failed:', refreshError);

                    // Force logout without redirecting via the auth plugin
                    auth.logout({
                        makeRequest: false,
                        redirect: false
                    });

                    // Manual redirect with query param for notification
                    router.push({
                        name: 'login',
                        query: { session_expired: 'true' }
                    });

                    throw refreshError;
                }
            }

            // NEW: Handle "Network Error" which might be a blocked 401 (CORS/Iframe)
            // If we sent a request with auth, and it failed with Network Error, 
            // and we are allowed to ignore auth redirects, try once as guest.
            if (!error.response && config.ignoreAuthRedirect && error.message === 'Network Error') {
                console.warn('[useAuthHttp] Network Error on public endpoint. Trying as guest.');
                // Only try ONCE to avoid loops
                if (!(config as any)._isGuestRetry) {
                    const retryConfig = {
                        ...defaultConfig, ...config,
                        _isGuestRetry: true, // Flag to prevent infinite loop
                        headers: { ...defaultConfig.headers, ...config.headers }
                    };
                    delete retryConfig.headers['Authorization'];

                    try {
                        // Force logout locally to clear the potential bad token
                        auth.logout({ makeRequest: false, redirect: false });

                        const guestResp = await auth.http({
                            ...retryConfig,
                            method: config.method || 'get',
                        });
                        return fullResponse ? guestResp : guestResp.data;
                    } catch (e) {
                        // Guest failed too? Throw original error
                        throw error;
                    }
                }
            }

            console.error('HTTP request error:', error);
            throw error;
        }
    };

    const get = async <T = any>(url: string, config: AxiosRequestConfig = {}, fullResponse = false): Promise<T | AxiosResponse<T>> => {
        return request<T>({ ...config, url, method: 'get' }, fullResponse);
    };

    const post = async <T = any>(url: string, data: any, config: AxiosRequestConfig = {}, fullResponse = false): Promise<T | AxiosResponse<T>> => {
        return request<T>({ ...config, url, method: 'post', data }, fullResponse);
    };

    const put = async <T = any>(url: string, data: any, config: AxiosRequestConfig = {}, fullResponse = false): Promise<T | AxiosResponse<T>> => {
        return request<T>({ ...config, url, method: 'put', data }, fullResponse);
    };

    const patch = async <T = any>(url: string, data: any, config: AxiosRequestConfig = {}, fullResponse = false): Promise<T | AxiosResponse<T>> => {
        return request<T>({ ...config, url, method: 'patch', data }, fullResponse);
    };

    const del = async <T = any>(url: string, config: AxiosRequestConfig = {}, fullResponse = false): Promise<T | AxiosResponse<T>> => {
        return request<T>({ ...config, url, method: 'delete' }, fullResponse);
    };

    return {
        get,
        post,
        put,
        patch,
        del,
    };
}
